import React, { useMemo } from "react";
import {
    StandardModalHeader,
    StandardModalFooter,
    PlainModal,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useDisableAccountsBulkMutation } from "@api/account.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { businessReachClause, useBusinessNamesFor } from "./reachCopy";

// Every constituent is archived on confirm; no in-modal picker.
interface AccountRowConstituent {
    id: string;
    name: string;
    entityId: number;
}

interface DisableChartOfAccountsModalProps {
    show?: boolean;
    onHide?: () => void;
    accounts: AccountRowConstituent[];
    // Editor override: when set, confirm runs this (the draft fan-out) instead of
    // the chart page's `disableAccountsBulk` mutation, so the draft and queue do
    // not desync.
    onConfirm?: () => void;
}

export const DisableChartOfAccountsModal: React.FC<
    DisableChartOfAccountsModalProps
> = ({ show, onHide, accounts, onConfirm }) => {
    const disableAccountsBulk = useDisableAccountsBulkMutation();
    const { toast, error: toastError } = useToaster();
    const businessNamesFor = useBusinessNamesFor();

    const uniqueEntityIds = useMemo(
        () => Array.from(new Set(accounts.map((account) => account.entityId))),
        [accounts],
    );

    const accountName = accounts[0]?.name ?? "";
    const reach = businessReachClause(businessNamesFor(uniqueEntityIds));

    const handleConfirm = async () => {
        if (onConfirm) {
            onConfirm();
            onHide?.();
            return;
        }
        const body = accounts.map((account) => ({
            accountId: account.id,
            entityId: account.entityId,
        }));
        try {
            const results = await disableAccountsBulk.mutateAsync(body);
            const successCount = results.filter((r) => r.success).length;
            const totalCount = results.length;
            if (successCount === totalCount) {
                toast(
                    `Archived ${pluralizeWithCount("account", successCount)}`,
                );
            } else {
                const failedCount = totalCount - successCount;
                toast(
                    `Archived ${pluralizeWithCount("account", successCount)}, ${failedCount} failed`,
                );
            }
            onHide?.();
        } catch {
            toastError("Failed to archive accounts");
        }
    };

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>
                Are you sure you want to archive this account?
            </StandardModalHeader>
            <p>
                If archived:
                <ul>
                    <li>
                        Existing transactions will remain mapped to this
                        account.
                    </li>
                    <li>
                        Future transactions that would have mapped here will go
                        to Uncategorized Income or Expenses unless updated
                        manually or covered by new Rules.
                    </li>
                    <li>
                        You will no longer be able to create journal entries in
                        this account.
                    </li>
                </ul>
                You can unarchive this account later if needed.
            </p>
            <p>{`This will archive ${accountName} in ${reach}.`}</p>
            <StandardModalFooter className="mt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onHide}
                    size="xl"
                    disabled={disableAccountsBulk.isPending}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    type="button"
                    loading={disableAccountsBulk.isPending}
                    size="xl"
                    onClick={handleConfirm}
                >
                    Archive
                </ButtonWithLoader>
            </StandardModalFooter>
        </PlainModal>
    );
};
